import { useCallback, useEffect, useMemo } from "react";
import { useHistory } from "react-router";
import {
  BaggageSelectors,
  SeatSelectors,
  useCheckoutSend,
} from "@checkout/index";
import { useCheckoutStateSelector as useSelector } from "@hopper-b2b/checkout";
import { logger } from "@hopper-b2b/datadog";
import { useI18nContext } from "@hopper-b2b/i18n";
import { ButtonWrap, Spinner } from "@hopper-b2b/ui";
import clsx from "clsx";
import Chevron from "../../../assets/client/arrow-back.svg?react";
import Baggage from "../../../assets/client/baggage-checked-bag.svg?react";
import Check from "../../../assets/client/empty-check.svg?react";
import Edit from "../../../assets/client/pencil.svg?react";
import Seat from "../../../assets/client/seat.svg?react";
import { BagsAndSeatsEventTypes } from "../../states/bagsAndSeats";
import { getIsBagsAndSeatsSelectionLoading } from "../../states/bagsAndSeats/selectors";
import styles from "./styles.module.scss";

export const BagsAndSeatsSelection = () => {
  const send = useCheckoutSend();
  const history = useHistory();
  const { t, formatFiatCurrency } = useI18nContext();

  const isLoading = useSelector(getIsBagsAndSeatsSelectionLoading);

  // Log when component mounts/enters bagsAndSeats state
  useEffect(() => {
    logger().info("BagsAndSeatsSelection: Component mounted/state entered", {
      event: "checkout_bags_and_seats_state_entered",
      isLoading,
      url: window.location.href,
    });
  }, [isLoading]);

  // Checkout only supports adding checked baggage
  const checkedBagPrices = useSelector(BaggageSelectors.getCheckedBagsPrices);
  // Baggage total cost
  const totalCombinedCost = useSelector(
    BaggageSelectors.extraBaggageCostTotalCombined
  );
  const totalBaggageCount = useSelector(
    BaggageSelectors.extraBaggageCountTotal
  );

  const cheapestSeat = useSelector(SeatSelectors.getCheapestSeat);
  const totalSeatPricing = useSelector(SeatSelectors.getSeatTotalPricing);
  const selectedSeats = useSelector(SeatSelectors.getSelectedSeats);

  const onBack = useCallback(() => {
    logger().info("BagsAndSeatsSelection: Back button clicked", {
      event: "checkout_bags_and_seats_back_clicked",
      url: window.location.href,
    });
    history.goBack();
  }, [history]);

  const hasCheckedBag = useMemo(() => {
    return totalBaggageCount > 0;
  }, [totalBaggageCount]);

  const hasSeat = useMemo(() => {
    return selectedSeats.length > 0;
  }, [selectedSeats]);

  const onContinue = useCallback(() => {
    logger().info("BagsAndSeatsSelection: Continue button clicked", {
      event: "checkout_bags_and_seats_continue_clicked",
      hasCheckedBag,
      hasSeat,
      totalBaggageCount,
      selectedSeatsCount: selectedSeats.length,
      url: window.location.href,
    });
    send(BagsAndSeatsEventTypes.NEXT);
  }, [send, hasCheckedBag, hasSeat, totalBaggageCount, selectedSeats.length]);

  const description = useMemo(() => {
    const description = {
      bag: t("bagsAndSeats.description", {
        price: formatFiatCurrency(checkedBagPrices?.[0]),
      }),
      seat: t("bagsAndSeats.description", {
        price: formatFiatCurrency(cheapestSeat?.fiat),
      }),
    };
    if (hasCheckedBag && totalCombinedCost.currencyCode) {
      const fiatPrice = {
        value: totalCombinedCost.total,
        currencyCode: totalCombinedCost.currencyCode,
      };
      description.bag = t("bagsAndSeats.hasBagsDescription", {
        count: totalBaggageCount,
        price: formatFiatCurrency(fiatPrice),
      });
    }
    if (hasSeat && typeof totalSeatPricing === "number") {
      const fiatPrice = {
        value: totalSeatPricing,
        currencyCode: cheapestSeat?.fiat?.currencyCode,
      };
      description.seat = t("bagsAndSeats.hasSeatsDescription", {
        count: selectedSeats.length,
        price: formatFiatCurrency(fiatPrice),
      });
    }
    return description;
  }, [
    cheapestSeat,
    checkedBagPrices,
    formatFiatCurrency,
    totalSeatPricing,
    totalCombinedCost,
    hasCheckedBag,
    totalBaggageCount,
    hasSeat,
    selectedSeats,
    t,
  ]);

  const continueLabel = useMemo(() => {
    switch (true) {
      case hasCheckedBag && hasSeat:
        return t("bagsAndSeats.continue");
      case hasCheckedBag:
        return t("bagsAndSeats.continueWithoutSeats");
      case hasSeat:
        return t("bagsAndSeats.continueWithoutBags");
      default:
        return t("bagsAndSeats.continueWithoutSelection");
    }
  }, [hasCheckedBag, hasSeat, t]);

  if (isLoading) {
    return (
      <div className={styles.Loading}>
        <Spinner />
      </div>
    );
  }

  return (
    <>
      <div className={styles.BagsAndSeatsSelectionHeader}>
        <ButtonWrap onClick={onBack}>
          <Chevron className="back" />
        </ButtonWrap>
      </div>
      <div className={styles.BagsAndSeatsSelection}>
        <h2 className="title">{t("bagsAndSeats.title")}</h2>
        <div className="products-section">
          {checkedBagPrices?.[0] ? (
            <ItemSection
              title={t("bagsAndSeats.bagsSectionTitle")}
              description={description.bag}
              icon={
                hasCheckedBag ? <Check className="checked-icon" /> : <Baggage />
              }
              onClick={() => {
                logger().info("BagsAndSeatsSelection: Select baggage clicked", {
                  event: "checkout_bags_and_seats_select_baggage_clicked",
                  url: window.location.href,
                });
                send(BagsAndSeatsEventTypes.SELECT_BAGGAGE);
              }}
              hasAdded={hasCheckedBag}
            />
          ) : null}
          {cheapestSeat?.fiat ? (
            <ItemSection
              title={t("bagsAndSeats.seatsSectionTitle")}
              description={description.seat}
              icon={hasSeat ? <Check className="checked-icon" /> : <Seat />}
              onClick={() => {
                logger().info("BagsAndSeatsSelection: Select seat clicked", {
                  event: "checkout_bags_and_seats_select_seat_clicked",
                  url: window.location.href,
                });
                send(BagsAndSeatsEventTypes.SELECT_SEAT);
              }}
              hasAdded={hasSeat}
            />
          ) : null}
        </div>
        <div className="bottom-action-section">
          <button
            onClick={onContinue}
            className={clsx("continue", {
              incomplete: !hasCheckedBag || !hasSeat,
            })}
          >
            {continueLabel}
          </button>
        </div>
      </div>
    </>
  );
};

const ItemSection = ({
  title,
  description,
  icon,
  onClick,
  hasAdded,
}: {
  title: string;
  description?: string;
  icon: React.ReactNode;
  onClick: () => void;
  hasAdded: boolean;
}) => {
  const { t } = useI18nContext();

  return (
    <div>
      <p className="section-title">{title}</p>
      <div className="section-content">
        <div className="section-description">
          {icon}
          {description && <p>{description}</p>}
        </div>
        {hasAdded ? (
          <ButtonWrap onClick={onClick}>
            <Edit />
          </ButtonWrap>
        ) : (
          <button onClick={onClick} className="add-product">
            {t("bagsAndSeats.addProduct")}
          </button>
        )}
      </div>
    </div>
  );
};
